export function mapClient(c) {
  return {
    clientId: c.id,
    clientName: c.name,
    industry: c.industry || '',
    region: c.region || '',
    status: c.status || 'Active',
    priority: c.priority || 'Standard',
    contractStart: c.contractStart || null,
    contractEnd: c.contractEnd || null,
    healthIndicator: c.healthIndicator || 'On Track',
    billingModel: c.billingModel || 'T&M',
    csm: c.csm || '',
    createdDate: c.createdAt ? c.createdAt.slice(0, 10) : '',
  };
}

export function mapProject(p) {
  return {
    projectId: p.id,
    projectName: p.name,
    // trackerProjectId === null means "situation-room-originated,
    // never pushed to tracker yet". The Delivery UI surfaces a "Push
    // to Tracker" button only in that case.
    trackerProjectId: p.trackerProjectId || null,
    clientId: p.clientId || p.client?.id || null,
    clientName: p.client?.name || '',
    // Keep the client nps relation so downstream views can coalesce
    // (Project.NPS override → Client.nps fallback). Post Phase B,
    // Project.NPS can be null — preserve the distinction here instead
    // of collapsing it to 0, otherwise the coalesce can never fire.
    clientNps: typeof p.client?.nps === 'number' ? p.client.nps : null,
    projectStatus: p.status || 'On Track',
    billables: p.billables || 0,
    NPS: typeof p.NPS === 'number' ? p.NPS : null,
    weeklyReportStatus: p.weeklyReportStatus || 'Pending',
    utilizationPct: p.utilizationPct || 0,
    contractStartDate: p.contractStartDate ? String(p.contractStartDate).slice(0, 10) : null,
    contractEndDate: p.contractEndDate ? String(p.contractEndDate).slice(0, 10) : null,
    deliveryDeadline: p.deliveryDeadline ? String(p.deliveryDeadline).slice(0, 10) : null,
    profitPct: p.profitPct || 0,
    ratePerHour: p.ratePerHour || 0,
    billingModel: p.billingModel || 'T&M',
    projectOwner: p.projectOwner || '',
    assignedConsultants: p.assignedConsultants || [],
    // Phase C: structured assignments (source of truth). Each entry carries
    // the assignment id (needed for DELETE), consultant id/name, allocation,
    // and role. The legacy String[] above stays around for any reader that
    // hasn't been migrated yet.
    assignments: Array.isArray(p.assignments)
      ? p.assignments.map((a) => ({
          id: a.id,
          consultantId: a.consultantId,
          consultantName: a.consultant?.name || '',
          allocationPct: a.allocationPct ?? 0,
          startDate: a.startDate ? String(a.startDate).slice(0, 10) : null,
          endDate: a.endDate ? String(a.endDate).slice(0, 10) : null,
          role: a.role || null,
        }))
      : [],
    lastClientCheckin: p.lastClientCheckin ? String(p.lastClientCheckin).slice(0, 10) : null,
    renewalProbability: p.renewalProbability || 0,
    riskNotes: p.riskNotes || '',
    estHours: p.estHours || 0,
    loggedHours: p.loggedHours || 0,
    hoursPerWeek: p.hoursPerWeek || 0,
    serviceType: p.serviceType || 'CONSULTING',
    growthStatus: p.growthStatus || 'STABLE',
    financialStatus: p.financialStatus || 'Stable',
    paymentSolution: p.paymentSolution || 'Hourly Invoicing',
    raincoach: p.raincoach || '',
    stakeholder: p.stakeholder || '',
    devReports: p.devReports || [],
    healthIndicator: p.healthIndicator || 'On Track',
    phase: p.phase || 'Delivery',
    // Tracker-critical fields surfaced for the edit drawer's completeness
    // check + mandatory enforcement. Null/empty means the PM must fill them
    // before Push to Tracker is enabled.
    budgetHours: typeof p.budgetHours === 'number' ? p.budgetHours : null,
    monthlyBudgetHours: typeof p.monthlyBudgetHours === 'number' ? p.monthlyBudgetHours : null,
    currency: p.currency || null,
    billingFrequency: p.billingFrequency || null,
    priority: p.priority || 'medium',
    projectType: p.projectType || 'development',
    origin: p.origin || 'tracker',
    // Tracker-owned actuals (BigClaude Phase 7). Rendered as read-only
    // rows in the Delivery drawer with a 🔒 from Tracker badge.
    actualCost: typeof p.actualCost === 'number' ? p.actualCost : null,
    actualEndDate: p.actualEndDate ? String(p.actualEndDate).slice(0, 10) : null,
  };
}

// Prisma TaskStatus / TaskPriority come through as SCREAMING_SNAKE_CASE.
// The rest of the app compares against human-readable labels (e.g. "Done",
// "Blocked", "In Progress", "Medium"). Normalize at the mapper boundary so
// every consumer works, and expose the reverse map for PATCH callers.
export const TASK_STATUS_ENUM_TO_DISPLAY = {
  TO_DO: 'To Do',
  IN_PROGRESS: 'In Progress',
  WAITING_ON_INTERNAL: 'Waiting on Internal',
  WAITING_ON_CLIENT: 'Waiting on Client',
  BLOCKED: 'Blocked',
  READY_FOR_REVIEW: 'Ready for Review',
  DONE: 'Done',
  CANCELLED: 'Cancelled',
};
export const TASK_STATUS_DISPLAY_TO_ENUM = Object.fromEntries(
  Object.entries(TASK_STATUS_ENUM_TO_DISPLAY).map(([k, v]) => [v, k])
);
export const TASK_PRIORITY_ENUM_TO_DISPLAY = {
  LOW: 'Low',
  MEDIUM: 'Medium',
  HIGH: 'High',
  CRITICAL: 'Critical',
};
export const TASK_PRIORITY_DISPLAY_TO_ENUM = Object.fromEntries(
  Object.entries(TASK_PRIORITY_ENUM_TO_DISPLAY).map(([k, v]) => [v, k])
);

export function taskStatusToEnum(value) {
  return TASK_STATUS_DISPLAY_TO_ENUM[value] || value;
}
export function taskPriorityToEnum(value) {
  return TASK_PRIORITY_DISPLAY_TO_ENUM[value] || value;
}

export function mapTask(t) {
  const rawStatus = t.status || 'TO_DO';
  const rawPriority = t.priority || 'MEDIUM';
  return {
    id: t.id,
    taskId: t.id,
    taskName: t.title,
    title: t.title,
    description: t.description || `${t.title} — required for project delivery.`,
    phaseId: t.phaseId || '',
    projectId: t.projectId || '',
    projectName: t.project?.name || '',
    clientName: t.project?.client?.name || '',
    department: t.department || '',
    assigneeId: t.assigneeId || t.assignee?.id || null,
    assignee: t.assignee?.name || '',
    backupAssignee: null,
    contributors: [],
    status: TASK_STATUS_ENUM_TO_DISPLAY[rawStatus] || rawStatus,
    priority: TASK_PRIORITY_ENUM_TO_DISPLAY[rawPriority] || rawPriority,
    dueDate: t.dueDate ? String(t.dueDate).slice(0, 10) : null,
    completedDate: t.completedDate ? String(t.completedDate).slice(0, 10) : null,
    notes: t.notes || '',
  };
}

export function mapDeal(d) {
  return {
    dealId: d.id,
    dealName: d.opportunityName,
    company: d.companyName || '',
    stage: d.stage || 'Lead Qualification',
    value: d.value || 0,
    // Default to SEK so unset deals render consistently with every other
    // "kr" display. Forms should capture currency explicitly when the
    // deal is negotiated in anything other than SEK.
    currency: d.currency || 'SEK',
    probability: d.probability || 0,
    closeDate: d.closeDate || null,
    type: d.dealType || 'New Business',
    source: d.leadSource || '',
    team: d.team || [],
    contacts: d.contacts || [],
    comments: d.dealComments || [],
    stageHistory: d.stageHistory || [],
  };
}

export function mapConsultant(c) {
  const firstName = c.firstName || '';
  const lastName = c.lastName || '';
  const name = (firstName && lastName) ? `${firstName} ${lastName}` : c.user?.name || c.name || '';
  const initials = name.split(' ').filter(Boolean).map(n => n[0]).join('').toUpperCase().slice(0, 2);
  const rawStatus = c.status || 'BENCH';
  const status = rawStatus.charAt(0) + rawStatus.slice(1).toLowerCase();
  const role = c.isAdmin ? (c.adminRole || c.role || '') : (['employee', 'admin'].includes((c.role || '').toLowerCase()) ? 'Developer' : c.role || 'Developer');
  return {
    consultantId: c.id,
    id: c.id,
    name,
    firstName,
    lastName,
    initials,
    role,
    isAdmin: c.isAdmin || false,
    adminRole: c.adminRole || null,
    dept: c.dept || 'Delivery',
    status,
    skills: Array.isArray(c.skills) ? c.skills : [],
    utilizationPct: c.utilizationPct || 0,
    currentUtilPct: c.currentUtilPct || 0,
    dailyCapacity: c.dailyCapacity || 8,
    notes: c.notes || '',
    costRate: c.costRate || 0,
    billRate: c.billRate || 0,
    employmentType: c.employmentType || 'Full Time',
    monthlyUtil: c.monthlyUtil || {},
    salary: c.salary || 0,
    joinDate: c.joinDate || (c.createdAt ? c.createdAt.slice(0, 10) : ''),
    // Resource lifecycle — when inactiveSince is set, UI excludes this
    // consultant from headcount / FT+PT rate / future utilisation calcs
    // from that date forward. Backend tracker queries do the same via
    // jsonb_array_elements on the inactive cutoff list.
    inactiveSince: c.inactiveSince ? String(c.inactiveSince).slice(0, 10) : null,
    inactiveReason: c.inactiveReason || null,
    vacationDaysTotal: c.vacationDaysTotal || 25,
    vacationDaysUsed: c.vacationDaysUsed || 0,
    plannedVacations: c.plannedVacations || [],
    billingHistory: Array.isArray(c.billingHistory) ? c.billingHistory : [],
    historicalUtil: c.historicalUtil || {},
    projectHistory: Array.isArray(c.projectHistory) ? c.projectHistory : [],
    resume: c.resume || null,
    certifications: Array.isArray(c.certifications) ? c.certifications : [],
    timeReports: Array.isArray(c.timeReports) ? c.timeReports : [],
    alvaLink: c.alvaLink || '',
    linkedIn: c.linkedIn || '',
    currentProjects: Array.isArray(c.currentProjects) ? c.currentProjects : [],
    availableFrom: c.availableFrom || null,
    performanceSummary: c.performanceSummary || null,
  };
}

export function mapWeeklyReport(r) {
  return {
    id: r.id,
    weekLabel: r.weekLabel || '',
    status: r.status || 'draft',
    generatedAt: r.generatedAt || r.createdAt || null,
    summary: r.summary || '',
    projectRows: Array.isArray(r.projectRows) ? r.projectRows : [],
    insights: Array.isArray(r.insights) ? r.insights : [],
    weekStart: r.weekStart || null,
    weekEnd: r.weekEnd || null,
  };
}

const SCOPING_STAGE_LABEL = {
  NEW:                '1. New',
  CUSTOMER_INFO:      '2. Customer Info',
  ESTIMATION:         '3. Estimation',
  PROPOSAL:           '4. Proposal',
  DEVELOPER_MATCHING: '5. Developer Matching',
  MEETING:            '6. Meeting',
  DONE:               '7. Done',
  LOST:               '8. Lost',
};

const SCOPING_PRIORITY_LABEL = {
  LOW:      'Low',
  MEDIUM:   'Medium',
  HIGH:     'High',
  CRITICAL: 'Critical',
};

export function mapScopingTask(t) {
  return {
    id:          t.id,
    title:       t.title,
    description: t.description || null,
    phase:       t.phase || 'scoping',
    done:        t.done,
    doneAt:      t.doneAt || null,
    dueDate:     t.dueDate ? (typeof t.dueDate === 'string' ? t.dueDate.slice(0, 10) : new Date(t.dueDate).toISOString().slice(0, 10)) : null,
    assigneeId:  t.assigneeId || null,
    // Flatten assignee to name string for UI display
    assignee:    t.assignee?.name || null,
    assigneeObj: t.assignee || null,
    comments:    Array.isArray(t.comments) ? t.comments : [],
    attachments: Array.isArray(t.attachments) ? t.attachments : [],
    subtasks:    Array.isArray(t.subtasks) ? t.subtasks.map(st => ({
      id:          st.id,
      title:       st.title,
      done:        st.done,
      doneAt:      st.doneAt || null,
      dueDate:     st.dueDate ? (typeof st.dueDate === 'string' ? st.dueDate.slice(0, 10) : new Date(st.dueDate).toISOString().slice(0, 10)) : null,
      assigneeId:  st.assigneeId || null,
      assignee:    st.assignee?.name || null,
      assigneeObj: st.assignee || null,
      comments:    Array.isArray(st.comments) ? st.comments : [],
      attachments: Array.isArray(st.attachments) ? st.attachments : [],
    })) : [],
  };
}

export function mapScoping(s) {
  const stage   = SCOPING_STAGE_LABEL[s.stage] || s.stage || '1. New';
  const priority= SCOPING_PRIORITY_LABEL[s.priority] || s.priority || null;
  const ownerId = s.owner?.id || null;
  const collaboratorsData = Array.isArray(s.collaborators) ? s.collaborators : [];
  const collaboratorIds = collaboratorsData.map(c => c.id);
  const tasks = Array.isArray(s.scopingTasks) ? s.scopingTasks.map(mapScopingTask) : [];
  const owner = s.owner?.name || tasks[0]?.assignee || null;

  return {
    // Identity
    id:                    s.id,
    name:                  s.name,
    dealName:              s.name,

    // Stage & priority
    stage,
    priority,

    // Monday board columns
    track:                 s.track || null,
    probability:           s.probability ?? null,
    dealValue:             s.dealValue ?? null,
    months:                Array.isArray(s.months) ? s.months : [],
    earliestDueDate:       s.earliestDueDate ? s.earliestDueDate.slice(0, 10) : null,
    leadActive:            s.leadActive || null,

    // People
    owner,
    ownerId,
    collaborators:         collaboratorsData,
    collaboratorIds,
    assignedTeam:          s.assignedTeam ? { id:s.assignedTeam.id, name:s.assignedTeam.name, stage:s.assignedTeam.stage } : null,

    // Dates
    startDate:             s.startDate ? s.startDate.slice(0, 10) : null,
    endDate:               s.endDate   ? s.endDate.slice(0, 10)   : null,

    // Developer matching
    matching: {
      matchNotes:        s.matchNotes || '',
      matchedDevelopers: Array.isArray(s.matchedDevelopers) ? s.matchedDevelopers : [],
    },

    // Financial planning (BigClaude Phase 4). Editable inputs surfaced as-is;
    // derived totals are computed live by lib/financials in the drawer, not
    // persisted here.
    hourlyRate:            typeof s.hourlyRate === 'number' ? s.hourlyRate : null,
    monthlyBudgetHours:    typeof s.monthlyBudgetHours === 'number' ? s.monthlyBudgetHours : null,
    currency:              s.currency || 'SEK',
    projectTitle:          s.projectTitle || null,

    // Relational tasks + stage history
    tasks,
    stageHistory:          Array.isArray(s.stageHistory) ? s.stageHistory : [],

    description: s.description || null,
  };
}

export function mapLead(l) {
  const normalizeTask = (task) => ({
    id: task.id,
    title: task.title || "",
    done: !!task.done,
    doneAt: task.doneAt || null,
    assigneeId: task.assigneeId || null,
    assignee: task.assignee || "",
    dueDate: task.dueDate || null,
    subtasks: Array.isArray(task.subtasks) ? task.subtasks.map((subtask) => ({
      id: subtask.id,
      title: subtask.title || "",
      done: !!subtask.done,
      doneAt: subtask.doneAt || null,
      assigneeId: subtask.assigneeId || null,
      assignee: subtask.assignee || "",
      dueDate: subtask.dueDate || null,
    })) : [],
  });

  const normalizeNote = (note) => ({
    id: note.id,
    text: note.text || "",
    createdAt: note.createdAt || null,
    authorId: note.authorId || note.author?.id || null,
    author: note.author?.name || "",
    initials: note.author?.initials || "",
    department: note.department || "SALES",
  });

  return {
    id: l.id,
    name: l.name,
    ownerId: l.ownerId || l.owner?.id || null,
    owner: l.owner?.name || '',
    status: l.status || 'NEW',
    referral: l.referral || '',
    industry: l.industry || '',
    region: l.region || '',
    estimatedValue: l.estimatedValue || '',
    contactPerson: l.contactPerson || '',
    contactEmail: l.contactEmail || '',
    stageHistory: Array.isArray(l.stageHistory) ? l.stageHistory : [],
    tasks: Array.isArray(l.tasks) ? l.tasks.map(normalizeTask) : [],
    notes: Array.isArray(l.notes) ? l.notes.map(normalizeNote) : [],
  };
}

export function mapAuditSubitem(s) {
  return {
    id:                    s.id,
    auditId:               s.auditId,
    name:                  s.name,
    email:                 s.email || '',
    primaryPosition:       s.primaryPosition || '',
    secondaryPosition:     s.secondaryPosition || '',
    questionnaireSent:     s.questionnaireSent ?? false,
    aktivTrackGuideSent:   s.aktivTrackGuideSent ?? false,
    atSent:                s.atSent ?? false,
    questionnaireDeadline: s.questionnaireDeadline ? s.questionnaireDeadline.slice(0, 10) : null,
    aktivTrakReturn:       s.aktivTrakReturn ? s.aktivTrakReturn.slice(0, 10) : null,
    interview:             s.interview ? s.interview.slice(0, 10) : null,
    interviewConfirmation: s.interviewConfirmation || '',
    orderIndex:            s.orderIndex ?? 0,
    comments:              Array.isArray(s.comments) ? s.comments : [],
  };
}

export function mapAuditTaskComment(comment) {
  return {
    id: comment.id,
    content: comment.content,
    userId: comment.userId,
    user: comment.user || null,
    createdAt: comment.createdAt,
    updatedAt: comment.updatedAt,
  };
}

export function mapAuditTask(task) {
  return {
    id: task.id,
    auditId: task.auditId,
    taskTemplateId: task.taskTemplateId || null,
    categoryId: task.categoryId || null,
    title: task.title,
    description: task.description || '',
    status: task.status || 'To Do',
    assigneeId: task.assigneeId || null,
    assignee: task.assignee?.name || '',
    assigneeUser: task.assignee || null,
    dueDate: task.dueDate ? task.dueDate.slice(0, 10) : '',
    createdAt: task.createdAt || null,
    updatedAt: task.updatedAt || null,
    category: task.category || null,
    taskTemplate: task.taskTemplate || null,
    comments: Array.isArray(task.comments) ? task.comments.map(mapAuditTaskComment) : [],
  };
}

export function mapTaskTemplate(template) {
  return {
    id: template.id,
    categoryId: template.categoryId,
    title: template.title,
    description: template.description || '',
    sortOrder: template.sortOrder ?? 0,
    defaultAssigneeId: template.defaultAssigneeId || '',
    defaultAssignee: template.defaultAssignee || null,
    defaultDueOffsetDays: template.defaultDueOffsetDays ?? '',
    active: template.active ?? true,
    createdAt: template.createdAt || null,
    updatedAt: template.updatedAt || null,
  };
}

export function mapTaskTemplateCategory(category) {
  return {
    id: category.id,
    name: category.name,
    description: category.description || '',
    active: category.active ?? true,
    createdAt: category.createdAt || null,
    updatedAt: category.updatedAt || null,
    templates: Array.isArray(category.templates) ? category.templates.map(mapTaskTemplate) : [],
  };
}

export function mapAudit(a) {
  return {
    id:           a.id,
    name:         a.name,
    type:         a.auditType?.name || a.type || 'Value Scan',
    contact:      a.contactUser?.name || a.contact || '',
    contactId:    a.contactId || null,
    contactUser:  a.contactUser || null,
    status:       a.status || 'Questionnaire',
    language:     a.auditLanguage?.name || a.language || '',
    languageId:   a.languageId || null,
    driveLink:    a.driveLink || '',
    lastUpdate:   a.lastUpdate ? a.lastUpdate.slice(0, 10) : '',
    estimator:    a.estimator || '',
    contract:     a.contract || '',
    transcripts:  a.transcripts || '',
    auditTypeId:  a.auditTypeId || null,
    auditType:    a.auditType || null,
    taskTemplateCategoryId: a.taskTemplateCategoryId || null,
    taskTemplateCategory: a.taskTemplateCategory || null,
    ownerId:      a.ownerId || null,
    owner:        a.owner || null,
    stageHistory: Array.isArray(a.stageHistory) ? a.stageHistory : [],
    tasks:        Array.isArray(a.tasks) ? a.tasks : [],
    auditTasks:   Array.isArray(a.auditTasks) ? a.auditTasks.map(mapAuditTask) : [],
    notes:        [],
    subitems:     Array.isArray(a.subitems) ? a.subitems.map(mapAuditSubitem) : [],
    comments:     Array.isArray(a.auditComments) ? a.auditComments : [],
    createdAt:    a.createdAt || null,
  };
}
